import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { getUsers, User, getStaffCategories, StaffCategory } from '../services/api';
import AttendanceReport from './reports/Attendance';
import AmbulanceRunningReport from './reports/VehicleReport';
import TripsReport from './reports/TripsReport';
import NotificationReport from './reports/NotificationReport';
import HaltReport from './reports/HaltReport';
import MessageLogsReport from './reports/MessageLogsReport';

type TabType = 'attendance' | 'vehicle' | 'trips' | 'notification' | 'halt' | 'messages';
const VALID_TABS: TabType[] = ['attendance', 'vehicle', 'trips', 'notification', 'halt', 'messages'];

const Reports: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as TabType | null;
  const [activeTab, setActiveTab] = useState<TabType>(
    (tabFromUrl && VALID_TABS.includes(tabFromUrl))
      ? tabFromUrl
      : 'attendance'
  );

  // Sync activeTab with URL params (e.g., when navigating from breadcrumb)
  useEffect(() => {
    const currentTab = searchParams.get('tab') as TabType | null;
    const validTab = currentTab && VALID_TABS.includes(currentTab)
      ? currentTab
      : 'attendance';
    
    if (validTab !== activeTab) {
      setActiveTab(validTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  // Update URL when tab changes (but only if it doesn't match current URL to avoid loops)
  useEffect(() => {
    const currentTab = searchParams.get('tab');
    const shouldHaveTab = activeTab !== 'attendance';
    const hasTab = !!currentTab;
    
    if (shouldHaveTab && currentTab !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
    } else if (!shouldHaveTab && hasTab) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  const [loading, setLoading] = useState(true);

  // Attendance
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [staffCategories, setStaffCategories] = useState<StaffCategory[]>([]);

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [usersRes, staffCategoriesRes] = await Promise.all([
        getUsers(1, 1000),
        getStaffCategories(1, 1000),
      ]);

      const users = usersRes.data.data || [];
      const categories = staffCategoriesRes.data.data || [];

      setAllUsers(users);
      setStaffCategories(categories);
    } catch (err: any) {
      console.error('Failed to load report data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="card">
          <div className="loading">Loading reports...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="card" style={{ marginTop: '24px', height: "calc(100vh - 185px)" }}>
        <div className="reports-tabs-container" style={{ display: 'flex', gap: '10px', marginBottom: '20px', paddingBottom: '10px' }}>
          <button
            onClick={() => setActiveTab('attendance')}
            className={`reports-tab ${activeTab === 'attendance' ? 'reports-tab-active' : ''}`}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: activeTab === 'attendance' ? 'bold' : 'normal',
            }}
          >
            Attendance
          </button>
          <button
            onClick={() => setActiveTab('vehicle')}
            className={`reports-tab ${activeTab === 'vehicle' ? 'reports-tab-active' : ''}`}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: activeTab === 'vehicle' ? 'bold' : 'normal',
            }}
          >
            Vehicle
          </button>
          <button
            onClick={() => setActiveTab('trips')}
            className={`reports-tab ${activeTab === 'trips' ? 'reports-tab-active' : ''}`}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: activeTab === 'trips' ? 'bold' : 'normal',
            }}
          >
            Trips
          </button>
          <button
            onClick={() => setActiveTab('notification')}
            className={`reports-tab ${activeTab === 'notification' ? 'reports-tab-active' : ''}`}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: activeTab === 'notification' ? 'bold' : 'normal',
            }}
          >
            Notification
          </button>
          <button
            onClick={() => setActiveTab('halt')}
            className={`reports-tab ${activeTab === 'halt' ? 'reports-tab-active' : ''}`}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: activeTab === 'halt' ? 'bold' : 'normal',
            }}
          >
            Halt
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`reports-tab ${activeTab === 'messages' ? 'reports-tab-active' : ''}`}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: activeTab === 'messages' ? 'bold' : 'normal',
            }}
          >
            Message Logs
          </button>
        </div>

        {activeTab === 'attendance' && (
          <AttendanceReport allUsers={allUsers} staffCategories={staffCategories} />
        )}

        {activeTab === 'vehicle' && (
          <AmbulanceRunningReport />
        )}

        {activeTab === 'trips' && (
          <TripsReport />
        )}

        {activeTab === 'notification' && (
          <NotificationReport />
        )}

        {activeTab === 'halt' && (
          <HaltReport />
        )}

        {activeTab === 'messages' && (
          <MessageLogsReport />
        )}
      </div>
    </DashboardLayout>
  );
};

export default Reports;
